import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@core/tenant';
import type { Reservation } from '../hooks/useUserReservations';
import { userReservationsKeys } from '../hooks/useUserReservations';
import { submitNameChange } from '../api/reservationApi';

interface ChangeNameViewProps {
  reservation: Reservation;
  onClose: () => void;
  onSuccess: () => void;
}

const ChangeNameView = ({ reservation, onClose, onSuccess }: ChangeNameViewProps) => {
  const { t } = useTranslation('profile');
  const { restaurant } = useTenant();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    try {
      await submitNameChange(reservation.id, name.trim());

      if (restaurant?.id) {
        await queryClient.invalidateQueries({
          queryKey: userReservationsKeys.byRestaurant(restaurant.id),
        });
      }

      onSuccess();
    } catch {
      setSubmitting(false);
      setError(t('MODIFY_RESERVATION_STRING_11_ERROR'));
    }
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full flex items-center mb-8">
        <button
          className="p-2 -ml-2 text-brand-dark hover:text-brand transition-colors"
          onClick={onClose}
          aria-label={t('MODIFY_RESERVATION_STRING_11_CONFIRM')}
        >
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-3xl font-display flex-1 text-center pr-8">
          {t('MODIFY_RESERVATION_STRING_11_TITLE')}
        </h2>
      </div>

      {reservation.guestName && (
        <p className="text-sm text-foreground/50 mb-1">{t('MODIFY_RESERVATION_STRING_11_CURRENT')}</p>
      )}
      {reservation.guestName && (
        <p className="text-lg font-semibold text-brand-dark mb-6">{reservation.guestName}</p>
      )}

      <form onSubmit={handleSubmit} className="w-full max-w-xs space-y-4">
        <div>
          <label className="block text-sm text-foreground/70 mb-2">
            {t('MODIFY_RESERVATION_STRING_11_INPUT_LABEL')}
          </label>
          <input
            type="text"
            className="w-full px-4 py-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand-dark transition-colors"
            placeholder={t('MODIFY_RESERVATION_STRING_11_PLACEHOLDER')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            autoFocus
          />
        </div>

        <p className="text-xs text-foreground/40">{t('MODIFY_RESERVATION_STRING_11_EMAIL')}</p>

        {error && (
          <p className="text-red-500 text-sm text-center">{error}</p>
        )}

        <button
          type="submit"
          className="w-full py-3 rounded-full bg-brand-dark text-brand-foreground font-medium text-sm tracking-wide hover:bg-brand transition-colors disabled:opacity-50"
          disabled={!canSubmit}
        >
          {submitting ? (
            <Loader2 size={18} className="animate-spin mx-auto" />
          ) : (
            t('MODIFY_RESERVATION_STRING_11_CONFIRM')
          )}
        </button>
      </form>
    </div>
  );
};

export default ChangeNameView;
